import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { BELO_PROVIDER } from "@/data/cityLocations";
import { DollarSign, TrendingUp, MapPin, ShieldCheck, Percent, Users, ChevronRight } from "lucide-react";

const SITE = "https://belorentals.com";

const STEPS = [
  { icon: MapPin, title: "List your vehicle", body: "Add your car in minutes — photos, details, and availability. Our team reviews and approves your listing." },
  { icon: DollarSign, title: "Set your own pricing", body: "You control your daily, weekly, and monthly rates. Choose a category to auto-fill standard prices, then adjust." },
  { icon: Users, title: "Connect with local renters", body: "Leverage our localized Massachusetts platform to reach verified renters right in your area." },
];

const BENEFITS = [
  { icon: Percent, title: "0 host fees", body: "We charge hosts nothing. You keep 100% of your set rental rate — no massive corporate platform take-rates." },
  { icon: TrendingUp, title: "Scale your fleet", body: "From a single car to a multi-car fleet, our tools and local demand help you grow revenue fast." },
  { icon: MapPin, title: "Community presence", body: "A real, local Massachusetts brand with physical storefronts renters trust." },
  { icon: ShieldCheck, title: "Marketing support", body: "We drive the traffic and marketing so your cars stay booked." },
];

const FAQS = [
  { q: "How do I list my car for rent in MA?", a: "Getting started is simple. List your vehicle, set your pricing, and leverage our localized Massachusetts platform to connect with verified renters right in your area." },
  { q: "How much does it cost to host?", a: "Nothing. Belo Rentals charges hosts 0 fees — you keep 100% of your set rental rate." },
  { q: "Can I manage a multi-car fleet?", a: "Absolutely. Whether you're a casual host offsetting a car payment or managing a large fleet in Central MA, our fleet-management tools scale with you." },
  { q: "Who handles payments and insurance?", a: "We collect payment securely and pay you out directly. Every trip is backed by our verification and protection processes." },
];

export default function ListYourCar() {
  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  const serviceLd = {
    "@context": "https://schema.org", "@type": "Service", serviceType: "Car sharing host platform",
    name: "List Your Car for Rent in Massachusetts",
    areaServed: { "@type": "State", name: "Massachusetts" },
    provider: { ...BELO_PROVIDER },
  };
  return (
    <div data-testid="list-your-car" className="bg-white">
      <Seo
        title="List Your Car for Rent in MA | Car Sharing Host Fleet Management"
        description="Turn your vehicle into an asset. List your car on Belo Rentals — Massachusetts' localized car-sharing marketplace. 0 host fees, keep 100% of your rate. Boston & Central MA hosts welcome."
        path="/list-your-car-massachusetts"
        keywords="list my car for rent MA, car sharing host Boston, Turo host fleet management Central MA, list your car Massachusetts, car sharing host Central MA"
        jsonLd={[faqLd, serviceLd]}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-5 sm:px-8 bg-gradient-to-b from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 text-sm font-medium mb-6">0 fees to hosts · Keep 100%</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">Earn Passive Income: List Your Car for Rent in MA</h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Turn your vehicle into an asset. Belo Rentals is Massachusetts' premier localized car-sharing marketplace, built to help independent owners and fleet managers maximize their revenue without the massive corporate platform fees.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/host" data-testid="list-car-cta" className="px-7 py-3.5 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors inline-flex items-center gap-2">List your car <ChevronRight size={18} /></Link>
            <a href="tel:+19782211451" className="px-7 py-3.5 rounded-full border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors">Call (978) 221-1451</a>
          </div>
        </div>
      </section>

      {/* How to list */}
      <section className="py-16 px-5 sm:px-8 max-w-6xl mx-auto">
        <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">How to List My Car for Rent in MA</h2>
        <p className="text-slate-600 mt-3 text-center max-w-2xl mx-auto">Getting started is simple. List your vehicle, set your pricing, and leverage our localized Massachusetts platform to connect with verified renters right in your area.</p>
        <div className="grid sm:grid-cols-3 gap-5 mt-10">
          {STEPS.map((s, i) => (
            <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-slate-900 text-white mb-4"><s.icon size={20} /></span>
              <h3 className="font-heading font-bold text-slate-900">{s.title}</h3>
              <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* The Belo advantage */}
      <section className="py-16 px-5 sm:px-8 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-slate-900">Car Sharing Host — Boston &amp; Central MA</h2>
          <p className="text-slate-600 mt-3 max-w-3xl leading-relaxed">
            Whether you are a casual car sharing host in Boston looking to offset your monthly car payment, or managing a large multi-car fleet in Central MA, Belo Rentals provides the community presence, lower take-rates, and marketing support you need to scale fast.
          </p>
          <h3 className="font-heading text-xl font-bold text-slate-900 mt-10">Turo Host Fleet Management Central MA: The Belo Advantage</h3>
          <p className="text-emerald-700 font-semibold mt-2">We charge 0 fees to the host. They keep 100% of their set rental rate.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-emerald-500 text-white mb-4"><b.icon size={20} /></span>
                <h3 className="font-heading font-bold text-slate-900">{b.title}</h3>
                <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-5 sm:px-8 max-w-3xl mx-auto">
        <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">Host FAQs</h2>
        <div className="mt-10 space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} data-testid={`host-faq-${i}`} className="bg-slate-50 rounded-xl border border-slate-200 p-5">
              <p className="font-semibold text-slate-900">{f.q}</p>
              <p className="text-slate-600 text-sm mt-2 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-center px-8 py-14">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">Ready to start earning?</h2>
          <p className="mt-4 text-emerald-50 max-w-xl mx-auto">List your car in minutes. 0 host fees — keep 100% of your rate.</p>
          <div className="mt-8">
            <Link to="/host" className="px-8 py-3.5 rounded-full bg-white text-emerald-700 font-semibold hover:bg-emerald-50 transition-colors inline-flex items-center gap-2">List your car <ChevronRight size={18} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
